import React, { useState, useEffect, useRef } from "react";
import { X, Download } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PWAInstallPrompt() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState("other");
  const [copied, setCopied] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return;
    if (window.location.pathname.startsWith("/admin")) return;

    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    const isSafari = isIOS && /safari/.test(ua) && !/crios|fxios|opios|edgios/.test(ua);

    if (isIOS && isSafari) setPlatform("ios-safari");
    else if (isIOS) setPlatform("ios-other");
    else setPlatform("android");

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    window.addEventListener("beforeinstallprompt", handler);

    const timer = setTimeout(() => setShow(true), 5000);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current;
    if (prompt) {
      prompt.prompt();
      const result = await prompt.userChoice;
      if (result.outcome === "accepted") {
        setShow(false);
        axios.post(`${API}/api/pwa/track-install`, { platform: "android", user_agent: navigator.userAgent }).catch(() => {});
      }
      deferredPromptRef.current = null;
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
    } catch {
      const input = document.createElement("input");
      input.value = window.location.origin;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  if (!show) return null;

  const ShareIcon = () => (
    <span className="inline-flex items-center bg-blue-100 rounded px-1 py-0.5 mx-1">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
    </span>
  );

  return (
    <div className="fixed bottom-4 left-3 right-3 z-[100] animate-[slideUp_0.3s_ease-out]" data-testid="pwa-install-prompt">
      <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgb(0,0,0,0.15)] border border-slate-200 p-4 max-w-md mx-auto relative">
        <button onClick={() => setShow(false)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" data-testid="pwa-dismiss">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <img src="/pwa-icon-192.png" alt="Ai Studdy Buddy" className="w-12 h-12 rounded-xl" />
          <div>
            <p className="font-bold text-[#0F1B3D] text-sm">Ai Studdy Buddy</p>
            <p className="text-slate-500 text-xs">Install app for quick access</p>
          </div>
        </div>

        {platform === "android" ? (
          /* Android — always show Install App button */
          <button
            onClick={handleInstall}
            data-testid="pwa-install-action-btn"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm py-3 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Install App
          </button>
        ) : platform === "ios-safari" ? (
          /* iPhone Safari */
          <div className="bg-orange-50 rounded-xl px-4 py-3 border border-orange-200 text-center">
            <p className="text-sm text-[#0F1B3D]">
              Tap <ShareIcon /> <span className="font-semibold">Share</span> in Safari below
            </p>
            <p className="text-xs text-slate-500 mt-1">Then tap <span className="font-bold">"Add to Home Screen"</span></p>
          </div>
        ) : platform === "ios-other" ? (
          /* iPhone Chrome/Firefox */
          <div className="space-y-2">
            <div className="bg-amber-50 rounded-xl px-4 py-3 border border-amber-200 text-center">
              <p className="text-sm text-[#0F1B3D] font-semibold">Open in Safari to install</p>
              <p className="text-xs text-slate-500 mt-1">Chrome on iPhone doesn't support app install. Open this link in <span className="font-bold">Safari</span>.</p>
            </div>
            <button
              onClick={copyLink}
              data-testid="pwa-copy-link-btn"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm py-3 rounded-xl transition-all active:scale-95"
            >
              {copied ? "Link Copied! Open Safari & Paste" : "Copy Link"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
